import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AuthService } from 'app/common/services/auth.service';
import { User } from 'app/common/models/user';
import { GetCurrenMenuExtras } from 'app/shared/interfaces/getCurrentMenuExtras';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class CoreMenuService {
  currentUser: User;
  onItemCollapsed: Subject<any>;
  onItemCollapseToggled: Subject<any>;

  // Private
  private _onMenuRegistered: BehaviorSubject<any>;
  private _onMenuUnregistered: BehaviorSubject<any>;
  private _onMenuChanged: BehaviorSubject<any>;
  private _currentMenuKey: string;
  private _registry: { [key: string]: any } = {};

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthService} authService
   */
  constructor(private _router: Router, private authService: AuthService) {
    this.authService.currentUser.subscribe(x => (this.currentUser = x));

    // Set defaults
    this.onItemCollapsed = new Subject();
    this.onItemCollapseToggled = new Subject();

    // Set private defaults
    this._currentMenuKey = null;
    this._onMenuRegistered = new BehaviorSubject(null);
    this._onMenuUnregistered = new BehaviorSubject(null);
    this._onMenuChanged = new BehaviorSubject(null);
  }

  // Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * onMenuRegistered
   *
   * @returns {Observable<any>}
   */
  get onMenuRegistered(): Observable<any> {
    return this._onMenuRegistered.asObservable();
  }

  /**
   * onMenuUnregistered
   *
   * @returns {Observable<any>}
   */
  get onMenuUnregistered(): Observable<any> {
    return this._onMenuUnregistered.asObservable();
  }

  /**
   * onMenuChanged
   *
   * @returns {Observable<any>}
   */
  get onMenuChanged(): Observable<any> {
    return this._onMenuChanged.asObservable();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register the provided menu with the provided key
   *
   * @param key
   * @param menu
   */
  register(key, menu): void {
    // Confirm if the key already used
    if (this._registry[key]) {
      console.error(`Menu with the key '${key}' already exist. Either unregister it first or use a unique key.`);

      return;
    }

    // Add to registry
    this._registry[key] = menu;

    // Notify subject
    this._onMenuRegistered.next([key, menu]);
  }

  /**
   * Unregister the menu from the registry
   *
   * @param key
   */
  unregister(key): void {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
    }

    // Unregister sidebar
    delete this._registry[key];

    // Notify subject
    this._onMenuUnregistered.next(key);
  }

  /**
   * Get menu from registry by key
   *
   * @param key
   * @returns {any}
   */
  getMenu(key): any {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    // Return sidebar
    return this._registry[key];
  }

  /**
   * Get current menu
   *
   * @returns {any}
   */
  
  getCurrentMenu(
    from_core_menu: boolean,
    extras?: GetCurrenMenuExtras
  ): any {
    if (!this._currentMenuKey) {
      console.warn('The current menu is not set.');
      return;
    }

    const registeredMenu = this.getMenu(this._currentMenuKey);

    if (!registeredMenu) {
      return;
    }

    let menuList = cloneDeep(registeredMenu);

    if (from_core_menu && extras) {
      if (extras.isAdmin) {

        if (!extras.canManualEncode) {
          menuList = menuList.filter(
            (m: any) => m.id !== 'manual-encoding'
          );
        }

        if (!extras.hasEFundAccess) {
          menuList = menuList.filter(
            (m: any) => m.id !== 'admin-efund'
          );
        }

        if (!extras.canCreateEFundUser && !extras.canCreateBenefit 
          && !extras.canAccessAdminSetup) {
          menuList = menuList.filter(
            (m: any) => m.id !== 'setup'
          );
        }

        const setup = menuList.find(
          (m: any) => m.id === 'setup'
        );

        if (setup?.children) {
          if (!extras.canCreateEFundUser) {
            setup.children = setup.children.filter(
              (c: any) => c.id !== 'eFundUserAccess'
            );
          }

          if (!extras.canCreateBenefit) {
            setup.children = setup.children.filter(
              (c: any) => c.id !== 'eFundBenefits'
            );
          }

          if (!extras.canAccessAdminSetup) {
            setup.children = setup.children.filter(
              (c: any) => !['membershipRequirements','regionMunicipalities'].includes(c.id)
            );
          }
        }

      } else {
        if (!extras.hasMemberCode) {
          menuList = menuList.filter(
            (m: any) => !['gam', 'digitalId'].includes(m.id)
          );
        }
      }
    }

    return menuList;
  }


  getCurrentRoleMenu(): any {
    if (!this._currentMenuKey) {
      console.warn(`The current menu is not set.`);

      return;
    }

    return this.getMenu(this._currentMenuKey).filter((m: any) => m.role.includes(this.currentUser?.roles[0]));
  }

  /**
   * Set menu with the key as the current menu
   *
   * @param key
   */
  setCurrentMenu(key): void {
    // Confirm if the sidebar exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    // Set current menu key
    this._currentMenuKey = key;

    // Notify subject
    this._onMenuChanged.next(key);
  }
}
